import { useState } from 'react';
import toast from 'react-hot-toast';
import { LockIcon } from '@phosphor-icons/react';

import { Button } from 'components/button/Button';
import { Input } from 'components/input/Input';
import { InputWrapper } from 'components/InputWrapper';
import { Card } from 'components/Card';
import { getDisplayError } from '@utils/get-display-error';
import { captureException } from '@sentry/react';
import { useTranslation } from 'contexts/translation-context';

const MIN_PASSWORD_LENGTH = 6;

type Props = {
  title: string;
  loginHint: string;
  licensePlate: string;
  codeName: string;
  onSavePassword: (password: string) => Promise<void>;
  onClear?: () => Promise<void>;
};

export function TruckAppCredentialsCard({ title, loginHint, licensePlate, codeName, onSavePassword, onClear }: Props) {
  const { i18n } = useTranslation();
  const [password, setPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const save = async (action: () => Promise<void>, successMessage: string) => {
    setIsSaving(true);
    try {
      await action();
      toast.success(successMessage);
      setPassword('');
    } catch (err) {
      captureException(err);
      toast.error(getDisplayError(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card title={title}>
      <div className="flex flex-col gap-4">
        <p className="text-xs text-fg-muted">{loginHint}</p>

        <div className="grid gap-3.5 sm:grid-cols-2">
          {/* The login is the licence plate — shown, never edited here. */}
          <InputWrapper labelText={i18n('carrierPortal.trucks.appLoginLabel')} hideInvalid>
            <Input type="text" value={licensePlate} onChange={() => {}} isDisabled />
          </InputWrapper>

          <InputWrapper labelText={i18n('carrierPortal.trucks.appPasswordLabel')} hideInvalid>
            <Input type="password" value={password} onChange={setPassword} />
          </InputWrapper>

          {codeName ? (
            <InputWrapper labelText={i18n('carrierPortal.trucks.appCodeNameLabel')} hideInvalid>
              <Input type="text" value={codeName} onChange={() => {}} isDisabled />
            </InputWrapper>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            color="primary"
            iconLeft={<LockIcon className="button-icon" />}
            isDisabled={isSaving || password.trim().length < MIN_PASSWORD_LENGTH}
            isLoading={isSaving}
            onTrigger={() => save(() => onSavePassword(password.trim()), i18n('carrierPortal.trucks.appPasswordSaved'))}
          >
            {i18n('carrierPortal.trucks.appSavePassword')}
          </Button>

          {onClear ? (
            <Button
              type="button"
              color="secondary"
              isDisabled={isSaving}
              isLoading={isSaving}
              onTrigger={() => save(onClear, i18n('carrierPortal.trucks.appLoginDisabled'))}
            >
              {i18n('carrierPortal.trucks.appDisableLogin')}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
